/**
 * sl-yphr: ElsterGateProvider
 * sl-y2po.12: Plan-Based Feature Gate Integration
 *
 * Provider component that:
 * 1. Reads plan-based availability AND connectivity from FeatureGateContext
 * 2. Toggles body.elster-enabled class when BOTH plan allows AND configured
 *
 * Mount inside FeatureGateProvider at workspace scope.
 *
 * SOLID:
 * - S: Only ELSTER body class gate management
 */

import { type ReactNode, useEffect } from 'react';

import { FEATURE_KEY_ELSTER, useFeatureGate } from '../hooks/use-feature-gate';

/**
 * CSS class name added to body when ELSTER is plan-allowed AND configured.
 * Used by isElsterEnabledFromBody() for Lit components.
 */
const ELSTER_ENABLED_CLASS = 'elster-enabled';

interface ElsterGateProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages global ELSTER state.
 * Reads plan + connectivity from the unified /features endpoint via FeatureGateContext.
 */
export function ElsterGateProvider({ children }: ElsterGateProviderProps) {
  const { isFeatureAllowed, isFeatureConfigured } = useFeatureGate();

  // sl-y2po.12: Combined gate — plan allows AND connectivity configured
  const isEnabled =
    isFeatureAllowed(FEATURE_KEY_ELSTER) &&
    isFeatureConfigured(FEATURE_KEY_ELSTER);

  // Toggle body class for Lit components
  useEffect(() => {
    document.body.classList.toggle(ELSTER_ENABLED_CLASS, isEnabled);

    return () => {
      document.body.classList.remove(ELSTER_ENABLED_CLASS);
    };
  }, [isEnabled]);

  return <>{children}</>;
}
